/**
 * ChatMessage — single message renderer.
 *
 * Renders assistant prose through react-markdown + remark-gfm so bold/lists/
 * tables/code blocks/links display natively. Brand-styled component overrides
 * keep the markdown output aligned with the rest of the app (zero rounded
 * corners, IBM Plex, brand color tokens).
 *
 * Tool results render via the component-registry mapping; unknown tools
 * fall through to a tight JSON dump.
 */
import { useState, type ComponentType } from 'react';
import { ChevronDown, ChevronUp, Copy, ExternalLink } from 'react-feather';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { api } from '../../../api/client';
import { useChatTelemetry } from '../hooks/useChatTelemetry';
import { componentRegistry, type ToolResultProps } from '../lib/component-registry';
import { ToolResultErrorBoundary } from './ToolResultErrorBoundary';
import { useChatStore, type ChatMessage as ChatMessageType } from '../../../stores/chatStore';
import { useUIStore } from '../../../stores/uiStore';
import type { Citation, CitationBlock, PageCitation } from '../types';

interface Props {
  message: ChatMessageType;
  /** Override the tool-result component map. Defaults to the panel registry.
   * The /chat full-window page passes a different map that mounts wider
   * components (e.g. the dashboard's VerdictCard) in place of the panel's
   * narrow renderers. */
  registry?: Record<string, ComponentType<ToolResultProps>>;
}

export function ChatMessage({ message, registry = componentRegistry }: Props) {
  const isUser = message.role === 'user';
  const { logEvent } = useChatTelemetry();
  const showToast = useUIStore((s) => s.showToast);
  const streamingPhase = useChatStore((s) => s.streamingPhase);
  const dealContext = useChatStore((s) => s.dealContext);

  // Defer card render until the prose has started streaming for this turn.
  // The Anthropic agent loop emits tool_result events BEFORE the model
  // generates its follow-up prose, so cards arrive ~1s before any text.
  // Without this gate, the user sees cards pop in first and an empty area
  // above them slowly fill with characters — visually backwards. Showing
  // cards once content is non-empty (or the stream is idle) puts the
  // intro sentence on screen first and the cards underneath as expected.
  const showCards = message.content.length > 0 || streamingPhase === 'idle';

  // V1.4.1 — synthetic resume messages emitted by UI control cards
  // (ChatDealCreationCard, ChatUploadCard, the two selection cards) are
  // sent to the backend so the model gets structured context, but they
  // contain raw internal data ("Deal created: ... (deal_id: ..., ...)")
  // and must NEVER appear in the visual thread.
  if (isUser && message.synthetic) {return null;}

  // Suppress empty assistant turns when the stream has ended without producing
  // any content or tool results (e.g. Anthropic overload mid-stream). The
  // ChatErrorBanner handles the error state — an orphan orange dot adds nothing
  // and confuses the user. Only suppress when idle: during 'thinking' the empty
  // bubble IS the loading skeleton.
  if (!isUser && !message.content && !message.toolResults?.length && streamingPhase === 'idle') {
    return null;
  }

  // User messages render right-aligned in a sharp-cornered tile (Claude-style
  // alignment, brand-rule sharp corners). Assistant messages stay full-bleed
  // left, no bubble — markdown + tool components own the visual density.
  if (isUser) {
    return (
      <div className="flex justify-end px-4 py-3" data-chat-message-id={message.id}>
        <div className="max-w-[80%] border border-border bg-surface-subtle px-3 py-2 text-body text-txt-primary">
          {message.content && <MarkdownContent>{message.content}</MarkdownContent>}
        </div>
      </div>
    );
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      showToast('Message copied', 'success');
    } catch {
      showToast('Could not copy', 'error');
    }
  };

  return (
    <div className="group bg-surface-main px-4 py-3" data-chat-message-id={message.id}>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span aria-hidden className="animate-credia-pulse inline-block h-2.5 w-2.5 rounded-full bg-orange" />
          <p className="text-body-sm font-semibold text-txt-primary">Credia</p>
        </div>
        {message.content && (
          <button
            type="button"
            onClick={handleCopy}
            aria-label="Copy message"
            title="Copy message"
            className="opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 p-1 text-txt-tertiary hover:text-txt-primary"
          >
            <Copy size={12} />
          </button>
        )}
      </div>
      {/* a11y: polite live region so screen readers hear the response stream
          in. Mounts empty during 'thinking', then announces appended deltas.
          Restored history mounts populated, so old turns are NOT re-announced
          on session switch. Prose only — label/Copy/cards stay outside. */}
      <div role="status" aria-live="polite" aria-atomic="false">
        {message.content && (
          <CitedMarkdown content={message.content} citations={message.citations} />
        )}
      </div>
      {/* Citations Phase 2 — footnote list with verbatim cited_text. Hidden
          during streaming so it doesn't flicker as citations arrive; once
          idle, the persisted/streamed citations render in numbered order.
          Falls through silently when message.citations is empty (resume of
          a pre-Phase-2 turn, or a turn where the model didn't cite). */}
      {!isUser
        && streamingPhase === 'idle'
        && message.citations
        && message.citations.length > 0
        && dealContext?.active_term_sheet_id != null && (
          <Citations
            citations={message.citations}
            dealId={dealContext?.deal_id ?? null}
            termSheetId={dealContext.active_term_sheet_id}
            onLogClick={logEvent}
            onError={(msg) => showToast(msg, 'error')}
          />
        )}
      {/* Citations Phase 1 — "View term sheet" link below every deal-context
          assistant message. Universal fallback for turns where a citation
          didn't land (Option C surface). Hidden during streaming so it
          doesn't pop in before the reply finishes. */}
      {!isUser
        && message.content.length > 0
        && streamingPhase === 'idle'
        && dealContext?.deal_id != null
        && dealContext?.active_term_sheet_id != null && (
          <ViewTermSheetLink
            dealId={dealContext.deal_id}
            termSheetId={dealContext.active_term_sheet_id}
            onLogClick={logEvent}
            onError={(msg) => showToast(msg, 'error')}
          />
        )}
      {showCards && (() => {
        // Suppress ListDealsCard whenever list_deals was used as a *lookup*
        // step rather than a genuine "show me my deals" intent. Lookups
        // happen when the user names a specific deal (model resolves
        // name → id via list_deals) or asks a portfolio query (fan-out).
        // Detect: list_deals + any deal-targeted follow-up tool in the
        // same turn. Rendering the picker UI alongside data results or
        // a term-sheet selector is confusing — the user already targeted
        // a specific deal.
        const DEAL_TARGETED_TOOLS = [
          'get_extraction',
          'get_deal',
          'get_analysis',
          'get_verdict',
          'get_benchmarks',
          'compare_term_sheets',
          'request_term_sheet_selection',
          'request_upload',
          'generate_export',
        ];
        const turnToolNames = (message.toolResults ?? []).map((t) => t.tool_name);
        const usedListDealsAsLookup =
          turnToolNames.includes('list_deals') &&
          DEAL_TARGETED_TOOLS.some((t) => turnToolNames.includes(t));

        return message.toolResults?.map((tr, i) => {
          if (usedListDealsAsLookup && tr.tool_name === 'list_deals') {return null;}
          const Renderer = registry[tr.tool_name];
          // Unmapped tools render nothing — the agent's prose carries the
          // meaning. Per visual-feedback addendum §6: components render OR
          // prose carries; never both, never raw JSON.
          if (!Renderer) {return null;}
          // Wrap each card in a single-shot fade+rise so it doesn't pop in.
          // ToolResultErrorBoundary catches a buggy renderer and emits the
          // component_render_error telemetry event without breaking the panel.
          return (
            <div key={i} className="animate-card-enter">
              <ToolResultErrorBoundary
                toolName={tr.tool_name}
                onError={(toolName, errorMessage) =>
                  logEvent('component_render_error', { tool_name: toolName, error_message: errorMessage })
                }
              >
                <Renderer result={tr.result} />
              </ToolResultErrorBoundary>
            </div>
          );
        });
      })()}
    </div>
  );
}

/**
 * CitedMarkdown — Citations Phase 2 wrapper.
 *
 * V1 simple path: render the prose through the existing MarkdownContent,
 * then append a single trailing superscript block listing each citation's
 * number ([1][2]…). Paragraph-level [N] injection would require rehype
 * AST surgery; the trailing group + the Citations footnote below covers
 * the Banker's "show me where" without ambiguity (the markers anchor to
 * the numbered footnote list).
 *
 * Resume-session safety: when `citations` is empty/undefined (pre-Phase-2
 * turns, no-citation V2 turns), this collapses to plain MarkdownContent
 * with no marker group.
 */
function CitedMarkdown({
  content,
  citations,
}: {
  content: string;
  citations: CitationBlock[] | undefined;
}) {
  const totalCount = (citations ?? []).reduce(
    (sum, b) => sum + b.citations.length,
    0,
  );
  return (
    <>
      <MarkdownContent>{content}</MarkdownContent>
      {totalCount > 0 && (
        <sup className="ml-1 text-caption text-orange">
          {Array.from({ length: totalCount }, (_, i) => `[${i + 1}]`).join('')}
        </sup>
      )}
    </>
  );
}

/**
 * Citations — Phase 2 footnote list rendered below the assistant prose.
 *
 * Flattens all CitationBlocks into a single numbered list. Each row is
 * clickable: requests a fresh signed URL via the Phase-1 endpoint, then
 * window.open with #page=N for page_location citations. cited_text is
 * rendered verbatim — NO translation, NO smartquote substitution, NO
 * markdown processing. That contract is enforced by SPEC ("citation =
 * document language always") — it's the bank-meeting defensibility
 * promise.
 */
function Citations({
  citations,
  dealId,
  termSheetId,
  onLogClick,
  onError,
}: {
  citations: CitationBlock[];
  dealId: number | null;
  termSheetId: number;
  onLogClick: (eventType: 'citation_clicked', payload: Record<string, unknown>) => void;
  onError: (message: string) => void;
}) {
  const [pendingIdx, setPendingIdx] = useState<number | null>(null);
  // Per-message collapse state. Sources start collapsed so a long reply
  // doesn't bury the prose under a wall of cited_text. Each Citations
  // instance owns its own useState — sibling messages remain independent.
  const [isExpanded, setIsExpanded] = useState(false);

  const flat: Citation[] = citations.flatMap((b) => b.citations);
  if (flat.length === 0) {return null;}

  const handleClick = async (
    e: React.MouseEvent<HTMLButtonElement>,
    citation: Citation,
    idx: number,
  ) => {
    // First line — defense against any anchor / React Router interception
    // higher in the tree. <button type="button"> already prevents form
    // submission, but preventDefault() also keeps any wrapping click
    // handler (e.g. router NavLink, click-anywhere-to-navigate) from
    // firing, which would otherwise redirect mid-click.
    e.preventDefault();
    if (pendingIdx !== null) {return;}
    setPendingIdx(idx);
    try {
      const { download_url } = await api.termSheets.getDownloadUrl(termSheetId);
      // PDF fragment URLs (#page=N) are honored by Chrome/Edge/Firefox
      // when the URL points directly at a PDF. Only meaningful for
      // page_location citations; for char/content_block we skip the
      // fragment and just open the doc.
      const url =
        citation.type === 'page_location'
          ? `${download_url}#page=${(citation as PageCitation).start_page_number}`
          : download_url;
      // Open the PDF FIRST. If the subsequent telemetry POST hits a 401
      // and the auth interceptor redirects to /login, the user already
      // has their source in a new tab — they don't lose the citation on
      // an auth blip.
      window.open(url, '_blank', 'noopener,noreferrer');
      onLogClick('citation_clicked', {
        deal_id: dealId,
        citation_type: citation.type,
      });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      onError(
        status === 410
          ? 'Source PDF is no longer available.'
          : 'Could not open the citation. Please try again.',
      );
    } finally {
      setPendingIdx(null);
    }
  };

  return (
    <div className="mt-3 border-t border-border pt-2">
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        aria-expanded={isExpanded}
        className="inline-flex items-center gap-1 text-caption uppercase tracking-wide text-txt-tertiary transition-colors hover:text-orange focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange"
      >
        Sources ({flat.length})
        {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {isExpanded && (
        <ol className="mt-2 space-y-1">
          {flat.map((c, i) => (
            <li key={i} className="text-body-sm text-txt-secondary">
              <button
                type="button"
                onClick={(e) => handleClick(e, c, i)}
                disabled={pendingIdx !== null}
                className="block w-full text-left transition-colors hover:text-orange focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange disabled:opacity-60"
              >
                <span className="font-medium text-txt-primary">
                  [{i + 1}] {citationLocationLabel(c)}:
                </span>{' '}
                {/* Verbatim source text — never translate, never normalize,
                    never markdown-process. SPEC §"cited_text language" hard
                    rule. Monospace + italic to mark "this is a literal
                    source quote, not the model's prose". */}
                <span className="font-mono italic">“{c.cited_text}”</span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function citationLocationLabel(c: Citation): string {
  if (c.type === 'page_location') {
    const { start_page_number: s, end_page_number: e } = c;
    return s === e ? `Term sheet, page ${s}` : `Term sheet, pages ${s}–${e}`;
  }
  if (c.type === 'char_location') {
    return `Term sheet, chars ${c.start_char_index}–${c.end_char_index}`;
  }
  // content_block_location
  return `Term sheet, block ${c.start_block_index}–${c.end_block_index}`;
}

/**
 * ViewTermSheetLink — Citations Phase 1 Option C fallback.
 *
 * Ghost button below an assistant message. On click, asks the backend for
 * a fresh signed URL (mints a new 1h URL every time so the link never
 * goes stale, even after a 24h dealContext rehydration) and opens it in
 * a new tab. Telemetry fires on click regardless of fetch outcome so we
 * can measure intent vs success separately.
 */
function ViewTermSheetLink({
  dealId,
  termSheetId,
  onLogClick,
  onError,
}: {
  dealId: number;
  termSheetId: number;
  onLogClick: (eventType: 'term_sheet_link_clicked', payload: Record<string, unknown>) => void;
  onError: (message: string) => void;
}) {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    if (isLoading) {return;}
    onLogClick('term_sheet_link_clicked', { deal_id: dealId, term_sheet_id: termSheetId });
    setIsLoading(true);
    try {
      const { download_url } = await api.termSheets.getDownloadUrl(termSheetId);
      window.open(download_url, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      // 410: PDF deleted post-analysis under GDPR retention. Anything else:
      // network / server hiccup. Either way the user retries or gives up.
      const status = (err as { response?: { status?: number } })?.response?.status;
      onError(
        status === 410
          ? 'Source PDF is no longer available.'
          : 'Could not open the term sheet. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      className="mt-2 inline-flex items-center gap-1.5 text-body-sm text-txt-tertiary transition-colors hover:text-orange focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange disabled:opacity-60"
    >
      <ExternalLink size={12} />
      {isLoading ? 'Opening…' : 'View term sheet'}
    </button>
  );
}

function MarkdownContent({ children }: { children: string }) {
  return (
    <div className="text-body text-txt-primary">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
          strong: ({ node, ...props }) => (
            <strong className="font-semibold text-txt-primary" {...props} />
          ),
          em: ({ node, ...props }) => <em className="italic" {...props} />,
          ul: ({ node, ...props }) => (
            <ul className="mb-2 ml-4 list-disc space-y-0.5 last:mb-0" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="mb-2 ml-4 list-decimal space-y-0.5 last:mb-0" {...props} />
          ),
          li: ({ node, ...props }) => <li className="text-body" {...props} />,
          h1: ({ node, ...props }) => (
            <h1 className="mb-2 text-h2 font-semibold text-txt-primary" {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2 className="mb-2 text-h3 font-semibold text-txt-primary" {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h3 className="mb-1 text-body font-semibold text-txt-primary" {...props} />
          ),
          a: ({ node, href, ...props }) => {
            const isExternal = typeof href === 'string' && /^https?:\/\//.test(href);
            return (
              <a
                href={href}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noreferrer' : undefined}
                className="text-orange hover:underline"
                {...props}
              />
            );
          },
          code: ({ node, className, children, ...props }) => {
            // Inline code if no language class; block code otherwise.
            const isBlock = /language-/.test(className ?? '');
            if (isBlock) {
              return (
                <code
                  className={`block whitespace-pre-wrap font-mono text-caption text-txt-primary ${className ?? ''}`}
                  {...props}
                >
                  {children}
                </code>
              );
            }
            return (
              <code
                className="bg-surface-subtle px-1 font-mono text-caption text-txt-primary"
                {...props}
              >
                {children}
              </code>
            );
          },
          pre: ({ node, ...props }) => (
            <pre
              className="mb-2 overflow-x-auto border border-border bg-surface-subtle p-3 last:mb-0"
              {...props}
            />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote
              className="mb-2 border-l-2 border-orange pl-3 text-txt-secondary last:mb-0"
              {...props}
            />
          ),
          table: ({ node, ...props }) => (
            <div className="mb-2 overflow-x-auto last:mb-0">
              <table className="w-full border-collapse text-body-sm" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => <thead className="bg-surface-subtle" {...props} />,
          th: ({ node, ...props }) => (
            <th
              className="border border-border px-2 py-1 text-left font-semibold text-txt-primary"
              {...props}
            />
          ),
          td: ({ node, ...props }) => (
            <td className="border border-border px-2 py-1 text-txt-primary" {...props} />
          ),
          hr: ({ node, ...props }) => <hr className="my-3 border-border" {...props} />,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

